import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Header } from "../components/Header";
import { ChevronLeft, ChevronRight, ArrowRight, Copy, RotateCcw, Save, Check, AlertTriangle, BookOpen, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `/api`;

export default function AccusationRetourneePage() {
  const [view, setView] = useState('intro'); // intro, exercise, examples
  const [examples, setExamples] = useState([]);

  useEffect(() => {
    axios.get(`${API}/accusation/examples`).then(r => setExamples(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/dashboard" className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200"><ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour</Link>

        {view === 'intro' && <IntroScreen onStart={() => setView('exercise')} onExamples={() => setView('examples')} />}
        {view === 'exercise' && <ExerciseFlow onBack={() => setView('intro')} />}
        {view === 'examples' && <ExamplesScreen examples={examples} onBack={() => setView('intro')} />}
      </main>
    </div>
  );
}

function IntroScreen({ onStart, onExamples }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-[#D9A892] to-[#C4866E] rounded-2xl flex items-center justify-center">
          <MessageCircle className="w-6 h-6 text-white" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-3xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }} data-testid="accusation-title">L'accusation retournée</h1>
          <p className="text-[#6A7771] text-sm">Relations / Communication / Conflits</p>
        </div>
      </div>

      <div className="space-y-6" style={{ fontFamily: 'Manrope' }}>
        <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8">
          <p className="text-[#2C3531] font-medium text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Quand vous accusez l'autre, il se défend.</p>
          
          <p className="text-[#5C6A61] leading-relaxed mb-4">Si vous dites :</p>
          <div className="bg-[#FAF9F6] rounded-2xl p-4 mb-5 space-y-1.5">
            <p className="text-[#2C3531] text-sm italic">"Tu me stresses."</p>
            <p className="text-[#2C3531] text-sm italic">"Tu m'empêches de dormir."</p>
            <p className="text-[#2C3531] text-sm italic">"Tu ne m'écoutes jamais."</p>
            <p className="text-[#2C3531] text-sm italic">"Tu ne fais aucun effort."</p>
          </div>
          <p className="text-[#5C6A61] leading-relaxed mb-6">L'autre risque d'entendre une attaque. Il peut alors se justifier, contre-attaquer, nier ou se fermer.</p>

          <p className="text-[#2C3531] font-medium mb-4" style={{ fontFamily: 'Outfit' }}>Mais si vous partez de vous-même, la dynamique change.</p>
          
          <p className="text-[#5C6A61] leading-relaxed mb-3">Au lieu de dire : "Tu es le problème."</p>
          <p className="text-[#5C6A61] leading-relaxed mb-1">Vous dites :</p>
          <div className="bg-[#6B8576]/5 rounded-2xl p-4 mb-5 space-y-1.5 border-l-3" style={{ borderLeft: '3px solid #6B8576' }}>
            <p className="text-[#2C3531] text-sm">"C'est moi qui ai une difficulté avec cette situation."</p>
            <p className="text-[#2C3531] text-sm">"Je monte vite en tension."</p>
            <p className="text-[#2C3531] text-sm">"J'ai le sommeil léger."</p>
            <p className="text-[#2C3531] text-sm">"Je suis sensible à ce sujet."</p>
            <p className="text-[#2C3531] text-sm">"J'ai besoin de faire autrement."</p>
          </div>

          <div className="space-y-2 mb-6">
            <p className="text-[#2C3531] text-sm font-medium">Ce n'est pas se rabaisser.</p>
            <p className="text-[#2C3531] text-sm font-medium">Ce n'est pas tout accepter.</p>
            <p className="text-[#2C3531] text-sm font-medium">Ce n'est pas excuser l'autre.</p>
          </div>
          <p className="text-[#5C6A61] leading-relaxed">C'est une stratégie pour éviter le combat et ouvrir la possibilité d'une coopération.</p>
        </div>

        {/* Phrase-clé */}
        <div className="bg-[#2A3A31] text-white rounded-3xl p-6 sm:p-8">
          <p className="text-white/90 leading-relaxed text-center" style={{ fontFamily: 'Outfit' }}>
            "Moins j'accuse l'autre, moins il se défend. Plus je parle de ma difficulté, plus il peut redevenir allié."
          </p>
        </div>

        {/* Boutons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={onStart} className="flex-1 py-4 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 flex items-center justify-center gap-2" data-testid="accusation-start">
            Commencer l'exercice <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button onClick={onExamples} className="flex-1 py-4 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium hover:bg-[#FAF9F6] transition-all duration-300 flex items-center justify-center gap-2" data-testid="accusation-examples-btn">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Voir les exemples
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function ExerciseFlow({ onBack }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    situation: '',
    accusation_directe: '',
    reactions_autre: [],
    reaction_autre_custom: '',
    accusation_retournee: '',
    action_concrete: '',
    phrase_finale: ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const reactions = ["Attaqué", "Accusé", "Coupable", "Injustement jugé", "Vexé", "Sur la défensive", "En colère", "Fermé"];
  const suggestions = [
    "Je suis sensible à...",
    "Je monte vite en tension quand...",
    "J'ai du mal avec...",
    "Je récupère mal quand...",
    "Je me rends compte que j'ai besoin de...",
    "Je suis probablement pénible avec ça, mais..."
  ];

  const steps = [
    { title: "La situation", desc: "Quelle est la situation qui vous dérange ?" },
    { title: "La phrase accusatoire", desc: "Quelle phrase auriez-vous envie de dire spontanément ?" },
    { title: "Ce que l'autre risque d'entendre", desc: "Si vous dites cette phrase, comment l'autre risque-t-il de se sentir ?" },
    { title: "Mon accusation retournée", desc: "Comment pouvez-vous ramener la phrase vers vous, sans vous écraser ?" },
    { title: "Mon action concrète", desc: "Que pouvez-vous faire concrètement pour prendre soin de vous ou de la situation ?" },
    { title: "Ma phrase finale", desc: "Votre formulation complète" }
  ];

  useEffect(() => {
    if (step === 5) {
      const auto = data.phrase_finale || `Je vais ${data.action_concrete || '[action concrète]'}. ${data.accusation_retournee || '[ma difficulté]'}`;
      if (!data.phrase_finale) setData(d => ({ ...d, phrase_finale: auto }));
    }
  }, [step]);

  const toggleReaction = (r) => {
    setData(d => ({
      ...d,
      reactions_autre: d.reactions_autre.includes(r) ? d.reactions_autre.filter(x => x !== r) : [...d.reactions_autre, r]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        situation: data.situation,
        accusation_directe: data.accusation_directe,
        reactions_autre: [...data.reactions_autre, ...(data.reaction_autre_custom ? [data.reaction_autre_custom] : [])],
        accusation_retournee: data.accusation_retournee,
        action_concrete: data.action_concrete,
        phrase_finale: data.phrase_finale
      };
      await axios.post(`${API}/accusation/save`, payload);
      setSaved(true);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(data.phrase_finale);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRestart = () => {
    setStep(0);
    setData({ situation: '', accusation_directe: '', reactions_autre: [], reaction_autre_custom: '', accusation_retournee: '', action_concrete: '', phrase_finale: '' });
    setSaved(false);
  };

  return (
    <div>
      <button onClick={onBack} className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour à l'introduction
      </button>

      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-8">
        {steps.map((_, i) => (
          <button key={i} onClick={() => i <= step && setStep(i)} className={`flex-1 h-2 rounded-full transition-all duration-300 ${i < step ? 'bg-[#6B8576]' : i === step ? 'bg-[#6B8576]/50' : 'bg-[#EBE7E0]'}`} data-testid={`accusation-progress-${i}`} />
        ))}
      </div>
      <p className="text-xs text-[#6A7771] mb-2">Étape {step + 1} / {steps.length}</p>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
          <h3 className="text-xl font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>{steps[step].title}</h3>
          <p className="text-sm text-[#6A7771] mb-6">{steps[step].desc}</p>

          {/* Step 1: Situation */}
          {step === 0 && (
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6">
              <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={3} placeholder="Exemple : Mon conjoint ronfle et je n'arrive pas à dormir." value={data.situation} onChange={e => setData(d => ({...d, situation: e.target.value}))} data-testid="accusation-situation" />
            </div>
          )}

          {/* Step 2: Phrase accusatoire */}
          {step === 1 && (
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6">
              <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={3} placeholder={"Exemple : Tu m'empêches de dormir avec tes ronflements."} value={data.accusation_directe} onChange={e => setData(d => ({...d, accusation_directe: e.target.value}))} data-testid="accusation-directe" />
              <p className="text-xs text-[#6A7771] mt-3 italic">Ici, écrivez la phrase brute, même si elle est maladroite ou agressive. Elle servira de point de départ.</p>
            </div>
          )}

          {/* Step 3: Reactions */}
          {step === 2 && (
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6">
              <div className="flex flex-wrap gap-2 mb-4">
                {reactions.map(r => (
                  <button key={r} onClick={() => toggleReaction(r)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${data.reactions_autre.includes(r) ? 'bg-[#6B8576] text-white' : 'bg-[#FAF9F6] border border-[#EBE7E0] text-[#2C3531] hover:bg-[#EBE7E0]/50'}`}
                    data-testid={`reaction-${r.toLowerCase().replace(/ /g, '-')}`}
                  >{r}</button>
                ))}
              </div>
              <div>
                <label className="block text-sm text-[#6A7771] mb-1.5">Autre :</label>
                <input type="text" className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300" placeholder="Précisez..." value={data.reaction_autre_custom} onChange={e => setData(d => ({...d, reaction_autre_custom: e.target.value}))} data-testid="reaction-autre" />
              </div>
            </div>
          )}

          {/* Step 4: Accusation retournée */}
          {step === 3 && (
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 space-y-4">
              <p className="text-xs text-[#6A7771] italic">Transformez "tu causes mon problème" en "j'ai une difficulté avec cette situation".</p>
              <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={3} placeholder="Exemple : Avec mon sommeil léger, je suis vite épuisée quand je dors mal." value={data.accusation_retournee} onChange={e => setData(d => ({...d, accusation_retournee: e.target.value}))} data-testid="accusation-retournee" />
              <div>
                <p className="text-xs text-[#6A7771] mb-2">Suggestions (cliquez pour ajouter) :</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => setData(d => ({...d, accusation_retournee: d.accusation_retournee + (d.accusation_retournee ? ' ' : '') + s}))}
                      className="px-3 py-1.5 bg-[#FAF9F6] border border-[#EBE7E0] rounded-full text-xs text-[#5C6A61] hover:bg-[#6B8576]/10 hover:border-[#6B8576]/30 transition-all duration-300"
                      data-testid={`suggestion-${i}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Action concrète */}
          {step === 4 && (
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6">
              <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={3} placeholder="Exemple : Je vais dormir à côté cette nuit pour récupérer." value={data.action_concrete} onChange={e => setData(d => ({...d, action_concrete: e.target.value}))} data-testid="accusation-action" />
              <p className="text-xs text-[#6A7771] mt-3 italic">Une bonne phrase ne sert pas seulement à expliquer. Elle indique aussi ce que vous allez faire.</p>
            </div>
          )}

          {/* Step 6: Phrase finale */}
          {step === 5 && (
            <div className="space-y-4">
              <div className="bg-[#2A3A31] text-white rounded-3xl p-6 sm:p-8">
                <p className="text-white/50 text-xs uppercase tracking-wider mb-3">Votre phrase finale</p>
                <textarea className="w-full bg-transparent text-white text-lg leading-relaxed border-none outline-none resize-none" rows={4} value={data.phrase_finale} onChange={e => setData(d => ({...d, phrase_finale: e.target.value}))} data-testid="accusation-finale" style={{ fontFamily: 'Outfit' }} />
              </div>

              <div className="bg-[#FAF9F6] rounded-2xl p-4 text-xs text-[#6A7771]">
                <p className="mb-1 font-medium text-[#2C3531]">Structure :</p>
                <p>"Je vais [action concrète]. Avec [ma difficulté], j'ai besoin de [besoin ou limite]."</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={handleCopy} className="flex-1 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium hover:bg-[#FAF9F6] transition-all duration-300 flex items-center justify-center gap-2 text-sm" data-testid="accusation-copy">
                  {copied ? <><Check className="w-4 h-4 text-[#6B8576]" strokeWidth={2} /> Copié !</> : <><Copy className="w-4 h-4" strokeWidth={1.5} /> Copier ma phrase</>}
                </button>
                <button onClick={handleSave} disabled={saving || saved} className="flex-1 py-3 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 flex items-center justify-center gap-2 text-sm disabled:opacity-60" data-testid="accusation-save">
                  {saved ? <><Check className="w-4 h-4" strokeWidth={2} /> Enregistré dans mon carnet</> : <><Save className="w-4 h-4" strokeWidth={1.5} /> Enregistrer dans mon carnet</>}
                </button>
              </div>
              <button onClick={handleRestart} className="w-full py-3 text-[#6A7771] hover:text-[#2C3531] transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-medium" data-testid="accusation-restart">
                <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Recommencer avec une autre situation
              </button>
            </div>
          )}

          {/* Navigation */}
          {step < 5 && (
            <div className="flex items-center justify-between mt-6">
              <button onClick={() => setStep(s => s - 1)} disabled={step === 0} className="px-5 py-2.5 text-sm font-medium text-[#6A7771] hover:text-[#2C3531] disabled:opacity-30 transition-colors duration-200 flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Précédent
              </button>
              <button onClick={() => setStep(s => s + 1)} className="px-5 py-2.5 bg-[#6B8576] text-white rounded-full text-sm font-medium hover:bg-[#546A5E] transition-all duration-300 flex items-center gap-1" data-testid="accusation-next">
                Suivant <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Warning box after step 6 */}
      {step === 5 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="font-semibold text-amber-800 mb-2" style={{ fontFamily: 'Outfit' }}>Attention</p>
              <p className="text-sm text-amber-700 leading-relaxed">
                Cet exercice n'est pas fait pour excuser une violence, une humiliation, une menace ou une manipulation.
              </p>
              <p className="text-sm text-amber-700 leading-relaxed mt-2">
                Si vous êtes en danger, si vous avez peur de la réaction de l'autre, ou si la relation vous abîme, l'objectif n'est pas de mieux formuler le problème. L'objectif est de vous protéger, de demander de l'aide et de poser une limite claire.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function ExamplesScreen({ examples, onBack }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <button onClick={onBack} className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour à l'introduction
      </button>
      
      <h2 className="text-2xl font-semibold text-[#2C3531] mb-6" style={{ fontFamily: 'Outfit' }}>Exemples</h2>
      
      <div className="space-y-4">
        {examples.map((ex, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="bg-white rounded-3xl border border-[#EBE7E0] p-5" data-testid={`example-${i}`}>
            <div className="flex gap-4">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-[#B86666] font-medium mb-1.5">Accusation directe</p>
                <p className="text-[#2C3531] text-sm italic mb-4">"{ex.direct}"</p>
              </div>
            </div>
            <div className="bg-[#6B8576]/5 rounded-2xl p-4" style={{ borderLeft: '3px solid #6B8576' }}>
              <p className="text-xs uppercase tracking-wider text-[#6B8576] font-medium mb-1.5">Accusation retournée</p>
              <p className="text-[#2C3531] text-sm">"{ex.retournee}"</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Warning */}
      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="font-semibold text-amber-800 mb-2" style={{ fontFamily: 'Outfit' }}>Attention</p>
            <p className="text-sm text-amber-700 leading-relaxed">
              Cet exercice n'est pas fait pour excuser une violence, une humiliation, une menace ou une manipulation.
            </p>
            <p className="text-sm text-amber-700 leading-relaxed mt-2">
              Si vous êtes en danger, si vous avez peur de la réaction de l'autre, ou si la relation vous abîme, l'objectif n'est pas de mieux formuler le problème. L'objectif est de vous protéger, de demander de l'aide et de poser une limite claire.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
